/**
 * chartTheme — tokens visuales compartidos por las cuatro vizzes de producción.
 *
 * Todo color que se pinte en un chart sale de aquí. Los componentes no hardcodean
 * hex: si mañana cambia la paleta del portal, se cambia en un solo lugar.
 *
 * Los valores replican los tokens de tailwind.config.js. No se pueden importar de
 * Tailwind en runtime (el config no es un módulo que el browser cargue), así que
 * se duplican a propósito y se mantienen en sync a mano.
 */

// ── Tokens espejo de tailwind.config.js ──────────────────────────────────────
export const NEUTRAL = {
  100: '#f5f5f5',
  200: '#e5e5e5',
  300: '#d4d4d4',
  400: '#a3a3a3',
  500: '#737373',
  600: '#525252',
  700: '#404040',
  900: '#171717',
};

export const PRIMARY_500 = '#3b82f6';
export const ERROR_500 = '#ef4444';

// ── Color por data entry ─────────────────────────────────────────────────────

/**
 * Paleta categórica para los data entries.
 *
 * Elegida para que los colores se distingan entre sí incluso en barras finas del
 * Gantt, y para que ninguno choque con el rojo de la línea de "hoy" ni con la
 * rampa naranja del heatmap.
 */
export const USER_PALETTE = [
  '#1e3a8a', // azul marino
  '#0ea5e9', // celeste
  '#f97316', // naranja
  '#16a34a', // verde
  '#a855f7', // morado
  '#eab308', // amarillo
  '#e11d48', // rosa fuerte
  '#0d9488', // teal
  '#737373', // gris
  '#7c2d12', // marrón
  '#4338ca', // índigo
  '#65a30d', // lima
];

/**
 * Overrides explícitos userId → color.
 *
 * `colorForUser` reparte por módulo, así que dos usuarios cuyos ids difieran en un
 * múltiplo del largo de la paleta comparten color. Cuando eso pase con dos personas
 * activas a la vez, se fija aquí el color de una de ellas.
 */
export const USER_COLOR_OVERRIDES = {
  // 42: '#16a34a',
};

/**
 * Color estable de un data entry.
 *
 * Deriva del userId y NO de la posición en el ranking, a propósito: si dependiera
 * del orden, el color de cada persona cambiaría al mover un filtro y el usuario
 * perdería la referencia visual entre las cuatro vizzes.
 *
 * @param {number|string} userId
 * @returns {string} hex
 */
export function colorForUser(userId) {
  if (USER_COLOR_OVERRIDES[userId]) return USER_COLOR_OVERRIDES[userId];

  const n = Number(userId);
  if (!Number.isFinite(n)) return NEUTRAL[400];

  return USER_PALETTE[Math.abs(Math.trunc(n)) % USER_PALETTE.length];
}

// ── Color por retailer ───────────────────────────────────────────────────────

/**
 * Paleta de retailers: la de usuarios sin el gris, que en una celda se lee como
 * "deshabilitado".
 */
const RETAILER_PALETTE = USER_PALETTE.filter((hex) => hex !== '#737373');

/**
 * Overrides retailer (minúsculas) → color. Amigo va en naranja como en el
 * Tableau original, que es la referencia visual de los usuarios.
 */
export const RETAILER_COLOR_OVERRIDES = {
  amigo: '#f97316',
};

/**
 * Color estable de un retailer (customername), derivado del nombre.
 *
 * Como `colorForUser`, no depende del orden ni de qué retailers estén visibles:
 * el mismo retailer se pinta igual en cualquier filtro. Normaliza mayúsculas y
 * espacios porque el dropdown de ProductsManager y `customername` no siempre
 * coinciden en ese detalle.
 *
 * @param {string} retailer
 * @returns {string} hex
 */
export function colorForRetailer(retailer) {
  const key = String(retailer ?? '').trim().toLowerCase();
  if (!key) return NEUTRAL[400];
  if (RETAILER_COLOR_OVERRIDES[key]) return RETAILER_COLOR_OVERRIDES[key];

  let h = 2166136261;
  for (let i = 0; i < key.length; i += 1) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return RETAILER_PALETTE[(h >>> 0) % RETAILER_PALETTE.length];
}

// ── Rampa de intensidad del heatmap ──────────────────────────────────────────

/**
 * Los tokens semánticos de Tailwind solo tienen shades 50/100/500/600/700, que no
 * alcanzan para una rampa continua. Se interpola sobre el naranja del Tableau
 * original: blanco (cero) → naranja saturado (máximo).
 */
const HEATMAP_FROM = { r: 255, g: 247, b: 237 };
const HEATMAP_TO = { r: 234, g: 88, b: 12 };

/**
 * Color de fondo de una celda del heatmap.
 *
 * Se usa raíz cuadrada en vez de escala lineal porque la distribución de entradas
 * por hora tiene mucha cola baja: en lineal, las horas de trabajo normal quedan
 * casi blancas y solo el pico se ve. La raíz levanta los valores medios.
 *
 * @param {number} value  conteo de la celda
 * @param {number} max    máximo del dataset visible
 * @returns {string} css color; transparente cuando no hay dato
 */
export function heatmapColor(value, max) {
  if (!value || value <= 0 || !max || max <= 0) return 'transparent';

  const t = Math.min(1, Math.sqrt(value / max));
  const mix = (a, b) => Math.round(a + (b - a) * t);

  return `rgb(${mix(HEATMAP_FROM.r, HEATMAP_TO.r)}, ${mix(HEATMAP_FROM.g, HEATMAP_TO.g)}, ${mix(HEATMAP_FROM.b, HEATMAP_TO.b)})`;
}

/**
 * Texto legible sobre una celda del heatmap: se oscurece el fondo pasada la mitad
 * de la rampa, así que a partir de ahí el número va en blanco.
 */
export function heatmapTextColor(value, max) {
  if (!value || !max) return NEUTRAL[400];
  return Math.sqrt(value / max) > 0.6 ? '#ffffff' : NEUTRAL[700];
}

// ── Formateadores ────────────────────────────────────────────────────────────
// Centralizados para que eje, etiqueta de barra y tooltip nunca muestren el mismo
// valor con dos formatos distintos.

const numberFmt = new Intl.NumberFormat('en-US');

/** 13039 → "13,039" */
export function formatCount(n) {
  const v = Number(n);
  return Number.isFinite(v) ? numberFmt.format(v) : '—';
}

/** 13039 → "13K" (para ejes, donde no cabe el número completo) */
export function formatCompact(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  if (Math.abs(v) < 1000) return String(v);
  if (Math.abs(v) < 1_000_000) return `${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}K`;
  return `${(v / 1_000_000).toFixed(1)}M`;
}

/** 0 → "12AM", 9 → "9AM", 13 → "1PM", 23 → "11PM" */
export function formatHour(hour) {
  const h = Number(hour);
  if (!Number.isFinite(h)) return '—';
  const suffix = h < 12 ? 'AM' : 'PM';
  const display = h % 12 === 0 ? 12 : h % 12;
  return `${display}${suffix}`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Parsea "YYYY-MM-DD" como fecha LOCAL, no UTC.
 *
 * `new Date('2026-09-02')` la interpreta como medianoche UTC, que en Puerto Rico
 * (UTC-4) es el 1 de septiembre a las 8PM — o sea, el día anterior. Ese corrimiento
 * es la fuente clásica de que un dashboard muestre todo desfasado un día.
 *
 * @param {string} isoDay
 * @returns {Date|null}
 */
export function parseDay(isoDay) {
  if (typeof isoDay !== 'string') return null;
  const [y, m, d] = isoDay.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

/** "2026-09-02" → "02-Sep-26" (el formato del Tableau original) */
export function formatDayShort(isoDay) {
  const date = parseDay(isoDay);
  if (!date) return '—';
  const dd = String(date.getDate()).padStart(2, '0');
  const yy = String(date.getFullYear()).slice(-2);
  return `${dd}-${MONTHS[date.getMonth()]}-${yy}`;
}

/** "2026-09-02" → "02-Sep Wed" (encabezado de columna del heatmap) */
export function formatDayWithWeekday(isoDay) {
  const date = parseDay(isoDay);
  if (!date) return '—';
  const dd = String(date.getDate()).padStart(2, '0');
  return `${dd}-${MONTHS[date.getMonth()]} ${WEEKDAYS[date.getDay()]}`;
}

/** "2026-09-02" → "Sep 2" (eje del Gantt) */
export function formatDayAxis(isoDay) {
  const date = parseDay(isoDay);
  if (!date) return '—';
  return `${MONTHS[date.getMonth()]} ${date.getDate()}`;
}

// ── Geometría compartida ─────────────────────────────────────────────────────
export const CHART_HEIGHT = {
  ranking: 260,
  period: 240,
};

export const AXIS_STYLE = {
  fontSize: 11,
  fill: NEUTRAL[500],
};

export const GRID_STYLE = {
  stroke: NEUTRAL[200],
  strokeDasharray: '3 3',
};
